import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { ConfigProvider } from "../../../config-provider.service";
import { MatError, MatFormField } from "@angular/material/form-field";
import { NgIf } from "@angular/common";
import { MatInput, MatInputModule } from "@angular/material/input";

@Component({
  selector: 'duration-control',
  providers: [],
  styleUrls: ['./duration-control.scss'],
  standalone: true,
  imports: [
    MatFormField,
    MatError,
    NgIf,
    ReactiveFormsModule,
    MatInputModule,
    TranslateModule,
  ],
  templateUrl: './duration-control.html'
})
export class DurationControlComponent implements AfterViewInit, OnDestroy {
    @Input() public set disabled(disabled: boolean) {
        this._disabled = disabled;
        if (disabled) {
            this.hoursFormControl.disable();
            this.minutesFormControl.disable();
            this.secondsFormControl.disable();
        } else {
            this.hoursFormControl.enable();
            this.minutesFormControl.enable();
            this.secondsFormControl.enable();
        }

    }
    public get disabled() {
        return this._disabled;
    }
    @Input() public set value(value: number) {
        this._value = value;
        if (!value && value !== 0) {
            this.hoursFormControl.setValue('');
            this.minutesFormControl.setValue('');
            this.secondsFormControl.setValue('');
            return;
        }

        this.parseValue(value);
    }
    public get value() {
        return this._value!;
    }
    public get invalid() {
        return this.hoursFormControl.invalid || this.minutesFormControl.invalid || this.secondsFormControl.invalid;
    }
    public readonly INPUT_MAX_LENGTH: number = 255;
    @Input() public displayMode: 'hms'|'hm'|'ms'|'s' = 'hms';
    @Input() public color: 'accent' | 'primary' | 'warn' = 'primary';
    @Input() public min: number | null = null; // seconds
    @Input() public max: number | null = null; // seconds
    @Input() public label = '';
    @Output() public valueChange: EventEmitter<number> = new EventEmitter<number>();
    @Output() public change: EventEmitter<number> = new EventEmitter<number>();

    public hoursFormControl: FormControl = new FormControl('', [
        (control: any) => this.checkHoursValidity()
    ]);
    public minutesFormControl: FormControl = new FormControl('', [
        (control: any) => this.checkMinutesValidity()
    ]);
    public secondsFormControl: FormControl = new FormControl('', [
        (control: any) => this.checkSecondsValidity()
    ]);
    private _disabled = false;
    private _value: number | null = null;
    private subscriptions: Subscription[] = [];
    constructor(
        public conf: ConfigProvider,
        private _router: Router,
        private _changeDetector: ChangeDetectorRef,
        private _translater: TranslateService
    ) {
    }

    public ngAfterViewInit() {
        this.subscriptions.push(
            this.hoursFormControl.valueChanges.subscribe((value: number) => this.updateValue())
        );
        this.subscriptions.push(
            this.minutesFormControl.valueChanges.subscribe((value: number) => this.updateValue())
        );
        this.subscriptions.push(
            this.secondsFormControl.valueChanges.subscribe((value: number) => this.updateValue())
        );
    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    }

    private parseValue(value: number): void {
        let hours = 0;
        let minutes = 0;
        let seconds = 0;

        if (['hms', 'hm'].indexOf(this.displayMode) >= 0) {
            hours = Math.floor(value / 3600);
            const _value: number = value % 3600;
            minutes = Math.floor(_value / 60);
            seconds = _value % 60;
        } else if (['ms'].indexOf(this.displayMode) >= 0) {
            minutes = Math.floor(value / 60);
            seconds = value % 60;
        } else if (['s'].indexOf(this.displayMode) >= 0) {
            seconds = value;
        }

        this.hoursFormControl.setValue(hours);
        this.minutesFormControl.setValue(minutes);
        this.secondsFormControl.setValue(seconds);
    }

    private checkHoursValidity(): any {
        if (!this.hoursFormControl) {
            return null;
        }

        if (this.min !== null && this._value! < this.min) {
            return { minValue: { valid: false } };
        }

        if (this.max !== null && this._value! > this.max) {
            return { maxValue: { valid: false } };
        }

        return null;
    }

    private checkMinutesValidity(): any {
        if (!this.minutesFormControl) {
            return null;
        }

        if (this.min !== null && this._value! < this.min) {
            return { minValue: { valid: false } };
        }

        if (this.max !== null && this._value! > this.max) {
            return { maxValue: { valid: false } };
        }

        if (['hms', 'hm'].indexOf(this.displayMode) >= 0) {
            if (this.minutesFormControl.value < 0 || this.minutesFormControl.value > 60) {
                return { invalidValue: { valid: false } };
            }
        }

        return null;
    }

    private checkSecondsValidity(): any {
        if (!this.secondsFormControl) {
            return null;
        }

        if (this.min !== null && this._value! < this.min) {
            return { minValue: { valid: false } };
        }

        if (this.max !== null && this._value! > this.max) {
            return { maxValue: { valid: false } };
        }

        if (['hms', 'ms'].indexOf(this.displayMode) >= 0) {
            if (this.secondsFormControl.value < 0 || this.secondsFormControl.value > 60) {
                return { invalidValue: { valid: false } };
            }
        }

        return null;
    }

    private updateValue(): void {
        this._value =
            (this.hoursFormControl.value * 3600) +
            (this.minutesFormControl.value * 60) +
            this.secondsFormControl.value;
        this.valueChange.emit(this._value!);
        this.change.emit(this._value!);
        this._changeDetector.detectChanges();
    }
}
